import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import MoreIcon from '@mulesoft/anypoint-icons/lib/svg/more-small.svg';
import ArrowDownIcon from '@mulesoft/anypoint-icons/lib/svg/arrow-down-small.svg';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Popover from '@mulesoft/anypoint-components/lib/Popover';
import MenuItem from '@mulesoft/anypoint-components/lib/MenuItem';
import { isScanInProgress } from '../../utils/utils';
import styles from './ScanConfigurationActions.css';

const ScanConfigurationActions = ({
  onRun,
  onAbort,
  onEdit,
  onDelete,
  scannerStatus,
  isMenuDisabled = false,
  variant = 'icon'
}) => {
  // Optimistic UI state: track what action should be shown next
  // 'run' = show "Run Now", 'abort' = show "Abort Scan", null = use scannerStatus
  const [optimisticAction, setOptimisticAction] = useState(null);

  // Reset the local override when scannerStatus updates from backend
  useEffect(() => {
    setOptimisticAction(null);
  }, [scannerStatus]);

  // Wrapped handler for "Run Now" to enable optimistic UI
  const handleRunClick = () => {
    setOptimisticAction('abort'); // After clicking run, show abort
    onRun();
  };

  // Wrapped handler for "Abort" to enable optimistic UI
  const handleAbortClick = () => {
    setOptimisticAction('run'); // After clicking abort, show run
    onAbort();
  };

  // Determine which action to show based on optimistic state or actual status
  const currentAction =
    optimisticAction || (isScanInProgress(scannerStatus) ? 'abort' : 'run');
  const shouldShowRun = currentAction === 'run';
  const shouldShowAbort = currentAction === 'abort';
  const renderTrigger = () => {
    if (variant === 'button') {
      return (
        <Button
          neutral
          className={styles.actionsButton}
          data-test-id="actions-button"
        >
          Actions
          <Icon size="xs" className={styles.chevronIcon}>
            <ArrowDownIcon />
          </Icon>
        </Button>
      );
    }

    return (
      <Icon
        className={styles.moreActionsIcon}
        aria-label="Show more actions"
        size="s"
        data-test-id="actions-icon"
      >
        <MoreIcon />
      </Icon>
    );
  };

  return (
    <div data-test-id="actions-id" className={styles.actions}>
      <Popover
        anchorPosition="br"
        popupPosition="tr"
        triggerOn={['click']}
        display-if={!isMenuDisabled}
        content={
          <div className={styles.actionsMenu}>
            {shouldShowRun && (
              <MenuItem onClick={handleRunClick}>
                <button type="button" className={styles.actionsMenuItem}>
                  Run Now
                </button>
              </MenuItem>
            )}
            {shouldShowAbort && (
              <MenuItem onClick={handleAbortClick}>
                <button type="button" className={styles.actionsMenuItem}>
                  Abort Scan
                </button>
              </MenuItem>
            )}
            <MenuItem onClick={onEdit}>
              <button type="button" className={styles.actionsMenuItem}>
                Edit Scanner
              </button>
            </MenuItem>
            <MenuItem data-test-id="actions-delete" onClick={onDelete}>
              <button
                type="button"
                className={classNames(
                  styles.actionsMenuItem,
                  styles.actionsMenuItemDelete
                )}
              >
                Delete Scanner
              </button>
            </MenuItem>
          </div>
        }
      >
        {renderTrigger()}
      </Popover>
    </div>
  );
};

ScanConfigurationActions.propTypes = {
  onRun: PropTypes.func.isRequired,
  onAbort: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  scannerStatus: PropTypes.string.isRequired,
  isMenuDisabled: PropTypes.bool,
  variant: PropTypes.oneOf(['icon', 'button'])
};

ScanConfigurationActions.defaultProps = {
  variant: 'icon'
};

export default ScanConfigurationActions;
